import React, { useEffect, useState } from "react";
import api from "../services/api";
import io from "socket.io-client";

const SOCKET_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('/api', '')
  : 'https://efit.com.do';

const socket = io(SOCKET_URL, {
  transports: ["websocket"],
});

const DashboardCaja = () => {
  const [ventas, setVentas] = useState([]);
  const [totalHoy, setTotalHoy] = useState(0);
  const [cantidadVentas, setCantidadVentas] = useState(0);
  const [ventasPorCaja, setVentasPorCaja] = useState({});
  const [ventasPorMetodo, setVentasPorMetodo] = useState({});
  const [estadisticas, setEstadisticas] = useState({
    promedioVenta: 0,
    ventaMaxima: 0,
    ventaMinima: 0
  });
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  // 🔄 FUNCIÓN PARA CARGAR DATOS INICIALES
  const cargarDatosIniciales = async () => {
    try {
      setCargando(true);
      setError(null);

      // 1. Cargar ventas del día
      const respuesta = await api.get('/ventas/dia');
      const datos = respuesta.data;

      // 2. Cargar reportes POS para métodos de pago
      try {
        const hoy = new Date().toISOString().split('T')[0];
        const reportesRespuesta = await api.get(`/ventas/reportes?fecha_inicio=${hoy}`);
        const reportes = reportesRespuesta.data;
        if (reportes.resumen) {
          setTotalHoy(reportes.resumen.monto_total || 0);
          setCantidadVentas(reportes.resumen.total_ventas || 0);
        }
      } catch (reportesError) {
        console.warn('⚠️ No se pudieron cargar reportes adicionales:', reportesError);
      }

      // 3. INICIALIZAR ESTADO CON DATOS REALES
      if (datos.ventas) {
        setVentas(datos.ventas);
        
        // Si no tenemos datos de reportes, calcular desde ventas
        if (datos.totalMonto !== undefined) {
          setTotalHoy(datos.totalMonto);
        } else {
          const totalCalculado = datos.ventas.reduce((sum, v) => sum + parseFloat(v.total || 0), 0);
          setTotalHoy(totalCalculado);
        }
        
        if (datos.totalVentas !== undefined) {
          setCantidadVentas(datos.totalVentas);
        } else {
          setCantidadVentas(datos.ventas.length);
        }
        
        // Procesar ventas por caja
        const ventasPorCajaProcesadas = procesarVentasPorCaja(datos.ventas);
        setVentasPorCaja(ventasPorCajaProcesadas);
        
        // Procesar métodos de pago
        const metodosProcesados = procesarMetodosPago(datos.ventas);
        setVentasPorMetodo(metodosProcesados);
        
        // Calcular estadísticas
        calcularEstadisticas(datos.ventas);
      } else if (datos.length > 0) {
        // Si la respuesta es un array directo de ventas
        setVentas(datos);
        const totalCalculado = datos.reduce((sum, v) => sum + parseFloat(v.total || 0), 0);
        setTotalHoy(totalCalculado);
        setCantidadVentas(datos.length);
        
        const ventasPorCajaProcesadas = procesarVentasPorCaja(datos);
        setVentasPorCaja(ventasPorCajaProcesadas);
        
        const metodosProcesados = procesarMetodosPago(datos);
        setVentasPorMetodo(metodosProcesados);
        
        calcularEstadisticas(datos);
      }
      
    } catch (err) {
      console.error('❌ Error cargando datos iniciales:', err);
      setError(err.message);
    } finally {
      setCargando(false);
    }
  };

  // 🔧 FUNCIÓN PARA PROCESAR VENTAS POR CAJA
  const procesarVentasPorCaja = (ventasArray) => {
    return ventasArray.reduce((acumulador, venta) => {
      const cajaId = venta.caja_id;
      
      if (!acumulador[cajaId]) {
        acumulador[cajaId] = {
          total: 0,
          cantidad: 0,
          ventas: []
        };
      }
      
      acumulador[cajaId].total += Number(venta.total || 0);
      acumulador[cajaId].cantidad += 1;
      acumulador[cajaId].ventas.push(venta);
      
      return acumulador;
    }, {});
  };

  // 🔧 FUNCIÓN PARA PROCESAR MÉTODOS DE PAGO
  const procesarMetodosPago = (ventasArray) => {
    return ventasArray.reduce((acumulador, venta) => {
      const metodo = venta.metodo_pago;
      if (metodo) {
        acumulador[metodo] = (acumulador[metodo] || 0) + Number(venta.total || 0);
      }
      return acumulador;
    }, {});
  };

  // 🔧 FUNCIÓN PARA CALCULAR ESTADÍSTICAS
  const calcularEstadisticas = (ventasArray) => {
    if (ventasArray.length === 0) {
      setEstadisticas({
        promedioVenta: 0,
        ventaMaxima: 0,
        ventaMinima: 0
      });
      return;
    }

    const montos = ventasArray.map(v => Number(v.total || 0));
    const promedio = montos.reduce((a, b) => a + b, 0) / montos.length;
    const maxima = Math.max(...montos);
    const minima = Math.min(...montos);

    setEstadisticas({
      promedioVenta: promedio,
      ventaMaxima: maxima,
      ventaMinima: minima
    });
  };

  // ⚡ EFFECT PRINCIPAL - CARGAR DATOS AL INICIAR
  useEffect(() => {
    cargarDatosIniciales();

    // Configurar recarga automática cada 5 minutos
    const intervalo = setInterval(() => {
      cargarDatosIniciales();
    }, 300000); // 5 minutos

    return () => clearInterval(intervalo);
  }, []);

  // 🎯 EFFECT - ESCUCHAR VENTAS EN TIEMPO REAL
  useEffect(() => {
    if (cargando) return;

    const manejarVentaNueva = (nuevaVenta) => {
      console.log("📡 Venta en tiempo real:", nuevaVenta);

      // 1. Agregar a la lista de ventas (máximo 100)
      setVentas(prev => [nuevaVenta, ...prev.slice(0, 99)]);

      // 2. Actualizar totales generales
      setTotalHoy(prev => prev + Number(nuevaVenta.total || 0));
      setCantidadVentas(prev => prev + 1);

      // 3. Actualizar ventas por caja
      setVentasPorCaja(prev => {
        const cajaId = nuevaVenta.caja_id;
        const infoAnterior = prev[cajaId] || {
          total: 0,
          cantidad: 0,
          ventas: []
        };

        return {
          ...prev,
          [cajaId]: {
            total: infoAnterior.total + Number(nuevaVenta.total || 0),
            cantidad: infoAnterior.cantidad + 1,
            ventas: [nuevaVenta, ...infoAnterior.ventas.slice(0, 9)] // Últimas 10
          }
        };
      });

      // 4. Actualizar métodos de pago
      setVentasPorMetodo(prev => ({
        ...prev,
        [nuevaVenta.metodo_pago]: (prev[nuevaVenta.metodo_pago] || 0) + Number(nuevaVenta.total || 0)
      }));

      // 5. Recalcular estadísticas
      setVentas(prevVentas => {
        const todasVentas = [nuevaVenta, ...prevVentas];
        calcularEstadisticas(todasVentas);
        return todasVentas.slice(0, 100); // Mantener máximo 100
      });
    };

    // Escuchar eventos del socket
    socket.on("venta_nueva", manejarVentaNueva);

    // Limpiar listeners al desmontar
    return () => {
      socket.off("venta_nueva", manejarVentaNueva);
    };
  }, [cargando]);

  // 🔄 FUNCIÓN PARA RECARGAR MANUALMENTE
  const recargarDatos = () => {
    cargarDatosIniciales();
  };

  // 🔐 FUNCIÓN PARA REDIRIGIR AL LOGIN
  const redirigirALogin = () => {
    window.location.href = '/login';
  };

  // 📊 CALCULAR MÉTRICAS ADICIONALES
  const metricasAdicionales = {
    efectivo: ventasPorMetodo.efectivo || 0,
    tarjeta: ventasPorMetodo.tarjeta || 0,
    transferencia: ventasPorMetodo.transferencia || 0,
    mixto: ventasPorMetodo.mixto || 0
  };

  // 🎨 RENDER PRINCIPAL
  if (cargando) {
    return (
      <div style={loadingContainer}>
        <div style={loadingContent}>
          <h2>📊 Cargando Dashboard...</h2>
          <p>Obteniendo datos del día en curso</p>
          <div style={spinner}></div>
        </div>
      </div>
    );
  }

  if (error) {
    const esErrorAuth = error.includes('token') || error.includes('sesión') || error.includes('401');
    
    return (
      <div style={errorContainer}>
        <div style={errorContent}>
          <h2>❌ {esErrorAuth ? 'Error de Autenticación' : 'Error al cargar datos'}</h2>
          <p>{error}</p>
          <div style={buttonContainer}>
            {esErrorAuth ? (
              <button onClick={redirigirALogin} style={loginButton}>
                🔑 Iniciar Sesión
              </button>
            ) : (
              <button onClick={recargarDatos} style={reloadButton}>
                🔄 Reintentar
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px", background: "#f5f5f5", minHeight: "100vh" }}>
      {/* HEADER CON BOTÓN DE RECARGA */}
      <div style={headerStyle}>
        <h2 style={{ fontSize: "26px", margin: 0, color: "#333" }}>
          🟢 Dashboard Cajas - Tiempo Real
        </h2>
        <button onClick={recargarDatos} style={reloadButton}>
          🔄 Actualizar
        </button>
      </div>

      <p style={subtitleStyle}>
        {ventas.length > 0 
          ? `Mostrando ${ventas.length} ventas del día - Actualizado: ${new Date().toLocaleTimeString()}`
          : 'No hay ventas registradas hoy'
        }
      </p>

      {/* 🔢 TARJETAS PRINCIPALES */}
      <div style={cardsContainer}>
        <div style={cardStyle}>
          <h3>💰 Total Vendido</h3>
          <p style={bigNumber}>{totalHoy.toFixed(2)} RD$</p>
          <small style={smallText}>{cantidadVentas} ventas</small>
        </div>

        <div style={cardStyle}>
          <h3>📦 Ventas Hoy</h3>
          <p style={bigNumber}>{cantidadVentas}</p>
          <small style={smallText}>Transacciones</small>
        </div>

        <div style={cardStyle}>
          <h3>📊 Promedio/Venta</h3>
          <p style={bigNumber}>{estadisticas.promedioVenta.toFixed(2)} RD$</p>
          <small style={smallText}>Ticket promedio</small>
        </div>

        <div style={cardStyle}>
          <h3>⚡ Venta Más Alta</h3>
          <p style={bigNumber}>{estadisticas.ventaMaxima.toFixed(2)} RD$</p>
          <small style={smallText}>Máximo del día</small>
        </div>
      </div>

      {/* 💳 MÉTODOS DE PAGO */}
      <div style={sectionStyle}>
        <h3 style={sectionTitle}>💳 Distribución por Método de Pago</h3>
        <div style={methodsContainer}>
          <div style={methodCard}>
            <h4>💵 Efectivo</h4>
            <p style={methodAmount}>{metricasAdicionales.efectivo.toFixed(2)} RD$</p>
            <small style={methodPercent}>
              {totalHoy > 0 ? ((metricasAdicionales.efectivo / totalHoy) * 100).toFixed(1) : 0}%
            </small>
          </div>
          
          <div style={methodCard}>
            <h4>💳 Tarjeta</h4>
            <p style={methodAmount}>{metricasAdicionales.tarjeta.toFixed(2)} RD$</p>
            <small style={methodPercent}>
              {totalHoy > 0 ? ((metricasAdicionales.tarjeta / totalHoy) * 100).toFixed(1) : 0}%
            </small>
          </div>
          
          <div style={methodCard}>
            <h4>📲 Transferencia</h4>
            <p style={methodAmount}>{metricasAdicionales.transferencia.toFixed(2)} RD$</p>
            <small style={methodPercent}>
              {totalHoy > 0 ? ((metricasAdicionales.transferencia / totalHoy) * 100).toFixed(1) : 0}%
            </small>
          </div>
          
          <div style={methodCard}>
            <h4>🔄 Mixto</h4>
            <p style={methodAmount}>{metricasAdicionales.mixto.toFixed(2)} RD$</p>
            <small style={methodPercent}>
              {totalHoy > 0 ? ((metricasAdicionales.mixto / totalHoy) * 100).toFixed(1) : 0}%
            </small>
          </div>
        </div>
      </div>

      {/* 🏧 VENTAS POR CAJA */}
      <div style={sectionStyle}>
        <h3 style={sectionTitle}>🏧 Ventas por Caja</h3>
        
        {Object.keys(ventasPorCaja).length === 0 ? (
          <p style={noDataText}>No hay ventas registradas en las cajas hoy</p>
        ) : (
          <div style={cajasContainer}>
            {Object.entries(ventasPorCaja).map(([cajaId, info]) => (
              <div key={cajaId} style={cajaCard}>
                <div style={cajaHeader}>
                  <h4>🏧 Caja #{cajaId}</h4>
                  <span style={badge}>{info.cantidad} ventas</span>
                </div>
                
                <p style={cajaTotal}>{info.total.toFixed(2)} RD$</p>
                
                <div style={cajaDetails}>
                  <strong>Últimas ventas:</strong>
                  <ul style={ventasList}>
                    {info.ventas.slice(0, 3).map((v, index) => (
                      <li key={index} style={ventaItem}>
                        <span>#{v.numero}</span>
                        <span>{Number(v.total).toFixed(2)} RD$</span>
                        <small>{new Date(v.fecha).toLocaleTimeString()}</small>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 📋 VENTAS EN VIVO */}
      <div style={sectionStyle}>
        <h3 style={sectionTitle}>
          📋 Ventas en Tiempo Real 
          <small style={{fontSize: '12px', marginLeft: '10px', color: '#666'}}>
            (Últimas {ventas.length} ventas)
          </small>
        </h3>
        
        {ventas.length === 0 ? (
          <p style={noDataText}>No hay ventas registradas hoy...</p>
        ) : (
          <div style={tableContainer}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th># Venta</th>
                  <th>Total</th>
                  <th>Método</th>
                  <th>Caja</th>
                  <th>Hora</th>
                </tr>
              </thead>
              <tbody>
                {ventas.map((v, i) => (
                  <tr key={i} style={i === 0 ? {...tableRow, background: '#f0fff0'} : tableRow}>
                    <td style={tableCell}><strong>#{v.numero}</strong></td>
                    <td style={tableCell}>{Number(v.total).toFixed(2)} RD$</td>
                    <td style={tableCell}>
                      <span style={methodBadge(v.metodo_pago)}>
                        {v.metodo_pago}
                      </span>
                    </td>
                    <td style={tableCell}>Caja {v.caja_id}</td>
                    <td style={tableCell}>
                      {new Date(v.fecha).toLocaleTimeString('es-DO')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// 🎨 ESTILOS MEJORADOS
const loadingContainer = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
  background: '#f5f5f5'
};

const loadingContent = {
  textAlign: 'center',
  padding: '40px'
};

const spinner = {
  width: '40px',
  height: '40px',
  border: '4px solid #f3f3f3',
  borderTop: '4px solid #059669',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
  margin: '20px auto'
};

const errorContainer = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
  background: '#f5f5f5'
};

const errorContent = {
  textAlign: 'center',
  padding: '40px'
};

const buttonContainer = {
  marginTop: '20px'
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '10px'
};

const subtitleStyle = {
  color: '#666',
  marginBottom: '20px',
  fontSize: '14px'
};

const reloadButton = {
  background: '#059669',
  color: 'white',
  border: 'none',
  padding: '8px 16px',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '14px'
};

const loginButton = {
  background: '#dc2626',
  color: 'white',
  border: 'none',
  padding: '10px 20px',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 'bold'
};

const cardsContainer = {
  display: "flex",
  gap: "15px",
  marginBottom: "20px",
  flexWrap: "wrap"
};

const cardStyle = {
  background: "#fff",
  padding: "20px",
  borderRadius: "12px",
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  minWidth: "200px",
  flex: "1",
  textAlign: "center"
};

const bigNumber = {
  fontSize: "24px",
  fontWeight: "bold",
  color: "#059669",
  margin: "10px 0 5px 0"
};

const smallText = {
  fontSize: "12px",
  color: "#666"
};

const sectionStyle = {
  background: "#fff",
  padding: "20px",
  borderRadius: "12px",
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  marginBottom: "20px"
};

const sectionTitle = {
  fontSize: "20px",
  marginBottom: "15px",
  color: "#333",
  borderBottom: "2px solid #f0f0f0",
  paddingBottom: "10px"
};

const methodsContainer = {
  display: "flex",
  gap: "15px",
  flexWrap: "wrap"
};

const methodCard = {
  background: "#fff",
  padding: "15px",
  borderRadius: "8px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  flex: "1",
  minWidth: "120px",
  textAlign: "center",
  border: "1px solid #f0f0f0"
};

const methodAmount = {
  fontSize: "16px",
  fontWeight: "bold",
  color: "#2563eb",
  margin: "5px 0 0 0"
};

const methodPercent = {
  fontSize: "12px",
  color: "#666",
  fontStyle: "italic"
};

const noDataText = {
  textAlign: "center",
  color: "#666",
  fontStyle: "italic",
  padding: "20px"
};

const cajasContainer = {
  display: "flex",
  gap: "15px",
  flexWrap: "wrap"
};

const cajaCard = {
  background: "#f8f9fa",
  padding: "15px",
  borderRadius: "8px",
  border: "1px solid #e9ecef",
  minWidth: "250px",
  flex: "1"
};

const cajaHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "10px"
};

const badge = {
  background: "#e9ecef",
  padding: "4px 8px",
  borderRadius: "12px",
  fontSize: "12px",
  fontWeight: "bold"
};

const cajaTotal = {
  fontSize: "18px",
  fontWeight: "bold",
  color: "#059669",
  margin: "10px 0"
};

const cajaDetails = {
  fontSize: "14px"
};

const ventasList = {
  listStyle: "none",
  padding: 0,
  margin: "10px 0 0 0"
};

const ventaItem = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "5px 0",
  borderBottom: "1px solid #f0f0f0"
};

const tableContainer = {
  overflowX: "auto"
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "14px"
};

const tableRow = {
  borderBottom: "1px solid #f0f0f0"
};

const tableCell = {
  padding: "12px 8px",
  textAlign: "left"
};

const methodBadge = (metodo) => ({
  padding: "4px 8px",
  borderRadius: "6px",
  fontSize: "12px",
  fontWeight: "bold",
  background: 
    metodo === 'efectivo' ? '#dcfce7' :
    metodo === 'tarjeta' ? '#dbeafe' :
    metodo === 'transferencia' ? '#fef3c7' : '#f3e8ff',
  color: 
    metodo === 'efectivo' ? '#166534' :
    metodo === 'tarjeta' ? '#1e40af' :
    metodo === 'transferencia' ? '#92400e' : '#7e22ce'
});

// Agregar animación CSS
const styles = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;

// Injectar estilos
const styleSheet = document.createElement('style');
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default DashboardCaja;